import { useLocalObservable } from 'mobx-react-lite';
import { runInAction } from 'mobx';
import { todayIso, firstDayOfNextMonth } from '@/lib/dateUtils';
import { updateCameraTariff } from '@/models/camera/service';
import { fetchTariff, fetchTariffs } from '@/models/tariff/service';
import { fetchCountries } from '@/models/country/service';
import type { Country } from '@/models/country/types';
import { currencySign } from '@/lib/currency';
import { tariffStore } from '@/stores/tariff.store';
import { toastStore } from '@/stores/toast.store';

export function useTariffModal(reloadServices: () => void) {
  const store = useLocalObservable(() => ({
    isOpen: false,
    idCamera: '',
    cameraName: '',
    currentTariffName: '',
    currentArchiveDays: 0,
    selectedArchiveDays: 0,
    accountTypeName: '',
    currencyLabel: '',
    countries: [] as Country[],
    get countryCurrencyMap(): Map<string, string> {
      return new Map(this.countries.map((c) => [c.name, currencySign(c)]));
    },
    selectedTariffId: '',
    date: '',
    description: '',
    isLoading: false,
    error: null as string | null,
    get filteredTariffs() {
      const currentName = this.currentTariffName.trim().toLowerCase();
      return tariffStore.managerTariffs.filter(
        (t) =>
          (!this.accountTypeName || t.account_type_name === this.accountTypeName) &&
          (!currentName || (t.tariff_name ?? '').trim().toLowerCase() !== currentName)
      );
    },
    get isArchiveDowngrade(): boolean {
      // Проверяем > 0, чтобы не срабатывало пока данные ещё не загружены.
      return (
        this.currentArchiveDays > 0 &&
        this.selectedArchiveDays > 0 &&
        this.selectedArchiveDays < this.currentArchiveDays
      );
    },
  }));

  function daysForPeriodId(idArchivePeriod: number): number {
    return (
      tariffStore.archiveDepths.find((a) => a.id_archive_period === String(idArchivePeriod))
        ?.days ?? 0
    );
  }

  function syncDate() {
    // При понижении глубины архива смена тарифа разрешена только с начала
    // следующего расчётного периода, чтобы не удалить уже записанный архив.
    store.date = store.isArchiveDowngrade ? firstDayOfNextMonth() : todayIso();
  }

  async function loadCurrentArchiveDays(tariffName: string) {
    // Ищем id тарифа по имени: сначала в кэше менеджера, потом в полном списке.
    // Затем запрашиваем детали тарифа, чтобы получить глубину архива.
    let tariffId = tariffStore.managerTariffs.find((t) => t.tariff_name === tariffName)?.id_tariff;
    if (tariffId == null) {
      const all = await fetchTariffs('0');
      tariffId = all.find((t) => t.tariff_name === tariffName)?.id_tariff;
    }
    if (tariffId == null) return;
    const detail = await fetchTariff(tariffId);
    const days = daysForPeriodId(detail.id_archive_period);
    runInAction(() => {
      store.currentArchiveDays = days;
      syncDate();
    });
  }

  async function loadSelectedArchiveDays(idTariff: string) {
    const detail = await fetchTariff(Number(idTariff));
    const days = daysForPeriodId(detail.id_archive_period);
    runInAction(() => {
      store.selectedArchiveDays = days;
      syncDate();
    });
  }

  function open(
    idCamera: string,
    cameraName: string,
    currentTariffName = '',
    accountTypeName = '',
    countryName = ''
  ) {
    void tariffStore.fetchForManager();
    void tariffStore.fetchArchiveDepths();
    void fetchCountries('1').then((countries) => {
      runInAction(() => {
        store.countries = countries;
        store.currencyLabel = currencySign(countries.find((c) => c.name === countryName));
      });
    });
    runInAction(() => {
      store.isOpen = true;
      store.idCamera = idCamera;
      store.cameraName = cameraName;
      store.currentTariffName = currentTariffName;
      store.currentArchiveDays = 0;
      store.selectedArchiveDays = 0;
      store.accountTypeName = accountTypeName;
      store.currencyLabel = '';
      store.selectedTariffId = '';
      store.date = todayIso();
      store.description = '';
      store.error = null;
    });
    if (currentTariffName) {
      void loadCurrentArchiveDays(currentTariffName);
    }
  }

  function close() {
    runInAction(() => {
      store.isOpen = false;
    });
  }

  function setSelectedTariffId(value: string) {
    runInAction(() => {
      store.selectedTariffId = value;
      store.selectedArchiveDays = 0;
    });
    if (value) {
      void loadSelectedArchiveDays(value);
    }
  }

  function setDate(value: string) {
    runInAction(() => {
      store.date = value;
    });
  }

  function setDescription(value: string) {
    runInAction(() => {
      store.description = value;
    });
  }

  function submit() {
    if (!store.selectedTariffId) {
      runInAction(() => {
        store.error = 'Выберите тариф';
      });
      return;
    }
    runInAction(() => {
      store.isLoading = true;
      store.error = null;
    });
    updateCameraTariff(store.idCamera, store.selectedTariffId, {
      date: store.date || undefined,
      description: store.description || undefined,
    })
      .then((result) => {
        const selectedCountry = tariffStore.managerTariffs.find(
          (t) => String(t.id_tariff) === store.selectedTariffId
        )?.country;
        const currency = store.countryCurrencyMap.get(selectedCountry ?? '') ?? store.currencyLabel;
        const msg =
          result.remain_pay !== 0
            ? `${result.message || 'Тариф успешно изменён'} · Остаток к оплате: ${result.remain_pay.toLocaleString('ru-RU')} ${currency}`
            : result.message || 'Тариф успешно изменён';
        runInAction(() => {
          store.isOpen = false;
          store.isLoading = false;
        });
        toastStore.show(msg, 'success');
        reloadServices();
      })
      .catch((err: Error) =>
        runInAction(() => {
          store.error = err.message;
          store.isLoading = false;
        })
      );
  }

  return {
    get isOpen() {
      return store.isOpen;
    },
    get idCamera() {
      return store.idCamera;
    },
    get cameraName() {
      return store.cameraName;
    },
    get currentTariffName() {
      return store.currentTariffName;
    },
    get isArchiveDowngrade() {
      return store.isArchiveDowngrade;
    },
    get selectedTariffId() {
      return store.selectedTariffId;
    },
    get date() {
      return store.date;
    },
    get description() {
      return store.description;
    },
    get isLoading() {
      return store.isLoading;
    },
    get error() {
      return store.error;
    },
    get filteredTariffs() {
      return store.filteredTariffs;
    },
    get currencyLabel() {
      return store.currencyLabel;
    },
    get countryCurrencyMap() {
      return store.countryCurrencyMap;
    },
    get minDate() {
      return store.isArchiveDowngrade ? firstDayOfNextMonth() : undefined;
    },
    open,
    close,
    setSelectedTariffId,
    setDate,
    setDescription,
    submit,
  };
}

export type TariffModal = ReturnType<typeof useTariffModal>;
